/**
 * SheetContextPanel
 *
 * Collapsible right-side rail rendered alongside the embedded Google Sheet
 * iframe in ``GoogleSheetWorkspacePanel``. Primary home for the per-sheet
 * "schema_context": description, per-tab header rows, columns, totals,
 * and column summaries authored once at ingestion (by the
 * ``evaluator_deep`` LLM) and refreshed only on structural change or a
 * user edit.
 *
 * UX notes:
 *   * Light-grey aesthetic on purpose — visually secondary to the sheet
 *     itself but always within reach.
 *   * Collapses to a 36px vertical strip so it does not steal real estate
 *     from the iframe.
 *   * Every "Save" from this panel flips the tab to
 *     ``locked=true / source="user_edit" / confidence=0.99``; from that
 *     point on the ingestion worker will not overwrite the user's
 *     correction on re-sync.
 *   * Totals are read-only — they are computed deterministically from
 *     the snapshot, not LLM-authored.
 */
import { ChevronRight, Check, Loader2, Lock, Unlock, PencilLine, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import googleSheetsIntegrationApi from '../../../api/googleSheetsIntegrationApi';

/**
 * Render a column's exact ``stats`` payload into a compact one-liner
 * used under the column row in the Context panel. Keeps parity with the
 * planner prompt format (sheet_qa_service._fmt_column_stats_line) so
 * users see the same view the agent sees.
 */
function formatColumnStats(stats: ColumnStats): string {
    const parts: string[] = [];
    if (typeof stats.count === 'number') parts.push(`n=${stats.count.toLocaleString()}`);
    if (typeof stats.null_count === 'number' && stats.null_count > 0)
        parts.push(`nulls=${stats.null_count.toLocaleString()}`);
    if (typeof stats.sum === 'number')
        parts.push(`sum=${stats.sum.toLocaleString(undefined, { maximumFractionDigits: 2 })}`);
    if (typeof stats.mean === 'number')
        parts.push(`mean=${stats.mean.toLocaleString(undefined, { maximumFractionDigits: 2 })}`);
    if (stats.min !== undefined && stats.min !== null && stats.min !== '')
        parts.push(
            `min=${
                typeof stats.min === 'number'
                    ? stats.min.toLocaleString(undefined, { maximumFractionDigits: 2 })
                    : String(stats.min)
            }`,
        );
    if (stats.max !== undefined && stats.max !== null && stats.max !== '')
        parts.push(
            `max=${
                typeof stats.max === 'number'
                    ? stats.max.toLocaleString(undefined, { maximumFractionDigits: 2 })
                    : String(stats.max)
            }`,
        );
    if (
        typeof stats.distinct_approx === 'number' &&
        stats.sum === undefined &&
        stats.min === undefined
    ) {
        parts.push(`distinct≈${stats.distinct_approx.toLocaleString()}`);
    }
    if (stats.sampled) parts.push('(column truncated)');
    return parts.join(' · ');
}

const COLUMN_TYPES = ['text', 'number', 'date', 'currency', 'bool'] as const;
type ColumnType = (typeof COLUMN_TYPES)[number];

interface ColumnStats {
    count?: number;
    null_count?: number;
    distinct_approx?: number;
    sum?: number;
    mean?: number;
    min?: number | string;
    max?: number | string;
    stddev?: number;
    rows_scanned?: number;
    sampled?: boolean;
}

interface ColumnSummary {
    name: string;
    type: ColumnType | string;
    samples: unknown[];
    /** Exact per-column stats pulled from Google Sheets at ingest. */
    stats?: ColumnStats;
}

interface TabSchema {
    header_rows?: number[];
    columns?: string[];
    data_start_row?: number;
    data_end_row?: number | null;
    notes?: string;
    confidence?: number;
    locked?: boolean;
    source?: string;
    totals?: {
        /** EFFECTIVE (non-empty) row count — what the agent quotes.
         * This is the count of data rows with any non-empty cell, not
         * the raw sheet grid size. */
        row_count?: number;
        col_count?: number;
        /** Raw Google Sheets grid size (``properties.gridProperties.rowCount``).
         * Usually larger than ``row_count`` — Sheets defaults to 1000
         * empty rows on a new file. Surfaced only as a faint hint. */
        grid_row_count?: number;
        /** Snapshot size. < row_count ⇒ agent narrative only saw a window. */
        sampled_row_count?: number;
    };
    /** Short, LLM-authored "what is this tab about?" narrative. Populated
     * at ingest time so summary-class questions can respond instantly
     * without re-deriving it from raw cells. */
    content_summary?: string;
    column_summaries?: ColumnSummary[];
    updated_at?: string;
    updated_by?: string;
}

interface SchemaContextResponse {
    description?: string;
    tabs?: Record<string, TabSchema>;
    history?: unknown[];
    updated_at?: string;
}

export interface SheetContextPanelProps {
    /** Mongo ``source_documents._id`` for the sheet. */
    sourceDocumentId: string;
    /**
     * Controlled open state. When undefined the panel self-manages
     * (collapsed by default).
     */
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

function ConfidencePill({ confidence, locked }: { confidence: number; locked: boolean }) {
    const pct = Math.round((confidence || 0) * 100);
    let tone = 'bg-gray-100 text-gray-700 border-gray-300';
    if (locked) tone = 'bg-emerald-50 text-emerald-700 border-emerald-200';
    else if (confidence >= 0.85) tone = 'bg-emerald-50 text-emerald-700 border-emerald-200';
    else if (confidence >= 0.6) tone = 'bg-amber-50 text-amber-700 border-amber-200';
    else tone = 'bg-rose-50 text-rose-700 border-rose-200';
    return (
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${tone}`}>
            {locked ? <Lock className="h-3 w-3" aria-hidden /> : null}
            {locked ? 'Locked' : `${pct}% confident`}
        </span>
    );
}
/**
 * Real exported panel. Owns data fetching, description editing, and
 * per-tab rendering. TabCard is kept colocated above but wrapped with a
 * tiny closure that injects the source doc id so the wire calls stay
 * tenant-correct.
 */
export default function SheetContextPanel({
    sourceDocumentId,
    open: controlledOpen,
    onOpenChange,
}: SheetContextPanelProps) {
    const [internalOpen, setInternalOpen] = useState(false);
    const open = controlledOpen ?? internalOpen;
    const setOpen = (next: boolean) => {
        if (controlledOpen === undefined) setInternalOpen(next);
        onOpenChange?.(next);
    };

    const [ctx, setCtx] = useState<SchemaContextResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [description, setDescription] = useState('');
    const [editingDesc, setEditingDesc] = useState(false);
    const [savingDesc, setSavingDesc] = useState(false);

    const load = useCallback(async () => {
        if (!sourceDocumentId) return;
        setLoading(true);
        setErr(null);
        try {
            const resp = await googleSheetsIntegrationApi.getSheetContext(sourceDocumentId);
            const context = (resp.schema_context || {}) as SchemaContextResponse;
            setCtx(context);
            setDescription(String(context.description || ''));
        } catch (e) {
            console.warn('getSheetContext failed', e);
            setErr('Could not load context.');
        } finally {
            setLoading(false);
        }
    }, [sourceDocumentId]);

    useEffect(() => {
        if (open) void load();
    }, [open, load]);

    const tabs = useMemo(() => {
        const entries = Object.entries(ctx?.tabs || {});
        entries.sort(([a], [b]) => a.localeCompare(b));
        return entries;
    }, [ctx]);

    const saveDescription = async () => {
        setSavingDesc(true);
        try {
            // ``patchSheetContext`` with any tab_name updates the
            // description as well; we piggy-back on the first tab so we
            // don't need a second endpoint.
            const firstTab = tabs[0]?.[0];
            if (!firstTab) return;
            const resp = await googleSheetsIntegrationApi.patchSheetContext(sourceDocumentId, {
                tab_name: firstTab,
                description,
            });
            setCtx((resp.schema_context || {}) as SchemaContextResponse);
            setEditingDesc(false);
        } catch (e) {
            console.warn('save description failed', e);
        } finally {
            setSavingDesc(false);
        }
    };

    if (!open) {
        return (
            <button
                type="button"
                onClick={() => setOpen(true)}
                className="flex h-full w-9 shrink-0 flex-col items-center justify-start gap-2 border-l border-gray-200 bg-[#f3f4f6] py-3 text-[11px] text-slate-600 transition hover:bg-gray-200"
                aria-label="Open sheet context"
                title="Open sheet context"
            >
                <ChevronRight className="h-4 w-4 rotate-180" aria-hidden />
                <span className="[writing-mode:vertical-rl] text-[11px] font-medium tracking-wide">
                    Context
                </span>
            </button>
        );
    }

    return (
        <aside
            className="flex h-full w-[340px] shrink-0 flex-col border-l border-gray-200 bg-[#f3f4f6]"
            aria-label="Sheet context"
        >
            <div className="flex items-center justify-between border-b border-gray-200 bg-[#f3f4f6] px-3 py-2">
                <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                        Sheet context
                    </p>
                    <p className="text-[11px] text-slate-500">
                        What the agent knows about this sheet.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="rounded-md border border-gray-200 bg-white p-1 text-slate-500 hover:bg-gray-100"
                    aria-label="Collapse sheet context"
                    title="Collapse"
                >
                    <ChevronRight className="h-4 w-4" aria-hidden />
                </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
                {loading && (
                    <p className="flex items-center gap-1 text-xs text-slate-500">
                        <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> Loading…
                    </p>
                )}
                {err && <p className="text-xs text-rose-600">{err}</p>}

                {!loading && !err && tabs.length === 0 && (
                    <p className="rounded-md border border-dashed border-gray-300 bg-white px-3 py-6 text-center text-xs text-slate-500">
                        Context will appear here after the first sync.
                    </p>
                )}

                {tabs.length > 0 && (
                    <div className="mb-3 rounded-md border border-gray-200 bg-white p-3">
                        <div className="mb-1 flex items-center justify-between">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                                Description
                            </p>
                            {!editingDesc ? (
                                <button
                                    type="button"
                                    onClick={() => setEditingDesc(true)}
                                    className="rounded-md border border-gray-200 bg-white p-1 text-slate-600 hover:bg-gray-100"
                                    aria-label="Edit description"
                                    title="Edit"
                                >
                                    <PencilLine className="h-3.5 w-3.5" aria-hidden />
                                </button>
                            ) : null}
                        </div>
                        {editingDesc ? (
                            <div className="space-y-2">
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    rows={3}
                                    placeholder="Short description of what this sheet is about…"
                                    className="w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-400"
                                />
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        disabled={savingDesc}
                                        onClick={saveDescription}
                                        className="inline-flex items-center gap-1 rounded-md border border-slate-800 bg-slate-900 px-2.5 py-1 text-[11px] font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {savingDesc ? (
                                            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                                        ) : (
                                            <Check className="h-3 w-3" aria-hidden />
                                        )}
                                        Save
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setEditingDesc(false);
                                            setDescription(String(ctx?.description || ''));
                                        }}
                                        className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 shadow-sm transition hover:bg-gray-100"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <p className="whitespace-pre-wrap text-xs text-slate-700">
                                {description || (
                                    <span className="text-slate-400">No description yet.</span>
                                )}
                            </p>
                        )}
                    </div>
                )}

                <div className="space-y-3">
                    {tabs.map(([tabName, schema]) => (
                        <BoundTabCard
                            key={tabName}
                            tabName={tabName}
                            schema={schema as TabSchema}
                            sourceDocumentId={sourceDocumentId}
                            onSaved={(next) => setCtx(next)}
                        />
                    ))}
                </div>
            </div>
        </aside>
    );
}

/**
 * Wrapper around ``TabCard`` that injects the real source document id
 * when the card calls ``patchSheetContext``. ``TabCard`` is kept dumb so
 * it's easy to unit-test in isolation.
 */
function BoundTabCard(props: {
    tabName: string;
    schema: TabSchema;
    sourceDocumentId: string;
    onSaved: (next: SchemaContextResponse) => void;
}) {
    const { tabName, schema, sourceDocumentId, onSaved } = props;
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [editing, setEditing] = useState(false);

    const [headerRowsStr, setHeaderRowsStr] = useState(
        (schema.header_rows || []).map((r) => r + 1).join(', '),
    );
    const [columnsStr, setColumnsStr] = useState((schema.columns || []).join('\n'));
    const [notes, setNotes] = useState(schema.notes || '');
    const [locked, setLocked] = useState(!!schema.locked);
    const [summaries, setSummaries] = useState<ColumnSummary[]>(
        (schema.column_summaries || []).map((cs) => ({ ...cs })),
    );

    useEffect(() => {
        setHeaderRowsStr((schema.header_rows || []).map((r) => r + 1).join(', '));
        setColumnsStr((schema.columns || []).join('\n'));
        setNotes(schema.notes || '');
        setLocked(!!schema.locked);
        setSummaries((schema.column_summaries || []).map((cs) => ({ ...cs })));
    }, [schema]);

    const handleSave = async () => {
        setSaving(true);
        setErr(null);
        try {
            const parsedHeaderRows = headerRowsStr
                .split(/[,\s]+/)
                .map((p) => p.trim())
                .filter((p) => p.length > 0)
                .map((p) => Number.parseInt(p, 10))
                .filter((n) => Number.isFinite(n) && n >= 1)
                .map((n) => n - 1);
            const parsedColumns = columnsStr
                .split('\n')
                .map((c) => c.trim())
                .filter((c) => c.length > 0);
            const resp = await googleSheetsIntegrationApi.patchSheetContext(sourceDocumentId, {
                tab_name: tabName,
                header_rows: parsedHeaderRows,
                columns: parsedColumns,
                notes,
                locked,
                column_summaries: summaries.map((cs) => ({
                    name: String(cs.name || ''),
                    type: String(cs.type || 'text'),
                    samples: Array.isArray(cs.samples) ? cs.samples.slice(0, 3) : [],
                    // Preserve ingest-time stats verbatim: the user
                    // can't edit them from this panel (they're exact
                    // from Sheets), and the backend patch endpoint
                    // should never re-invent them.
                    ...(cs.stats ? { stats: cs.stats } : {}),
                })),
            });
            onSaved((resp.schema_context || {}) as SchemaContextResponse);
            setEditing(false);
        } catch (e) {
            console.warn('patchSheetContext failed', e);
            setErr('Could not save. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const rowCount = schema.totals?.row_count;
    const colCount = schema.totals?.col_count;
    const sampledRowCount = schema.totals?.sampled_row_count;
    const sampledNote =
        typeof rowCount === 'number' &&
        typeof sampledRowCount === 'number' &&
        sampledRowCount < rowCount
            ? `Agent narrative sampled ${sampledRowCount.toLocaleString()} of ${rowCount.toLocaleString()} rows; column stats below are exact over the full sheet.`
            : null;
    const confidence = schema.confidence || 0;

    return (
        <div className="rounded-md border border-gray-200 bg-white p-3">
            <div className="mb-2 flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-900">{tabName}</p>
                    <p className="mt-0.5 text-[11px] text-slate-500">
                        {rowCount != null && colCount != null
                            ? `${rowCount.toLocaleString()} rows × ${colCount} cols`
                            : 'Totals not yet computed'}
                        {schema.source ? ` · ${schema.source}` : ''}
                    </p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                    <ConfidencePill confidence={confidence} locked={!!schema.locked} />
                    {!editing ? (
                        <button
                            type="button"
                            onClick={() => setEditing(true)}
                            className="rounded-md border border-gray-200 bg-white p-1 text-slate-600 hover:bg-gray-100"
                            aria-label={`Edit context for ${tabName}`}
                            title="Edit"
                        >
                            <PencilLine className="h-3.5 w-3.5" aria-hidden />
                        </button>
                    ) : (
                        <button
                            type="button"
                            disabled={saving}
                            onClick={() => setEditing(false)}
                            className="rounded-md border border-gray-200 bg-white p-1 text-slate-500 hover:bg-gray-100"
                            aria-label="Cancel"
                            title="Cancel"
                        >
                            <X className="h-3.5 w-3.5" aria-hidden />
                        </button>
                    )}
                </div>
            </div>

            {!editing ? (
                <div className="space-y-2 text-xs text-slate-700">
                    <div>
                        <span className="text-slate-500">Header row(s):</span>{' '}
                        {(schema.header_rows || []).map((r) => r + 1).join(', ') || '—'}
                    </div>
                    {sampledNote && (
                        <div className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
                            {sampledNote}
                        </div>
                    )}
                    {schema.notes && (
                        <div className="text-slate-600">
                            <span className="text-slate-500">Notes:</span> {schema.notes}
                        </div>
                    )}
                    {(schema.column_summaries || []).length > 0 && (
                        <div className="mt-2 overflow-hidden rounded border border-gray-200">
                            <table className="w-full table-fixed text-[11px]">
                                <thead className="bg-gray-100 text-slate-600">
                                    <tr>
                                        <th className="px-2 py-1 text-left font-medium">Column</th>
                                        <th className="w-[68px] px-2 py-1 text-left font-medium">Type</th>
                                        <th className="px-2 py-1 text-left font-medium">Samples</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(schema.column_summaries || []).map((cs, idx) => {
                                        const s = cs.stats;
                                        const hasStats = !!s && Object.keys(s).length > 0;
                                        return (
                                            <React.Fragment key={`${cs.name}:${idx}`}>
                                                <tr className="border-t border-gray-100">
                                                    <td className="truncate px-2 py-1 font-medium text-slate-800">
                                                        {cs.name}
                                                    </td>
                                                    <td className="px-2 py-1 text-slate-600">
                                                        {String(cs.type)}
                                                    </td>
                                                    <td className="truncate px-2 py-1 text-slate-500">
                                                        {(cs.samples || [])
                                                            .slice(0, 3)
                                                            .map((v) => String(v))
                                                            .join(', ')}
                                                    </td>
                                                </tr>
                                                {hasStats && (
                                                    <tr className="border-t border-gray-50 bg-gray-50/60">
                                                        <td
                                                            colSpan={3}
                                                            className="px-2 py-1 text-[10.5px] text-slate-500"
                                                        >
                                                            {formatColumnStats(s!)}
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            ) : (
                <div className="space-y-3">
                    <label className="block text-[11px] font-medium text-slate-600">
                        Header row(s) <span className="text-slate-400">(1-based; e.g. 1, 2)</span>
                        <input
                            type="text"
                            value={headerRowsStr}
                            onChange={(e) => setHeaderRowsStr(e.target.value)}
                            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                    </label>
                    <label className="block text-[11px] font-medium text-slate-600">
                        Columns <span className="text-slate-400">(one per line)</span>
                        <textarea
                            value={columnsStr}
                            onChange={(e) => setColumnsStr(e.target.value)}
                            rows={Math.max(4, Math.min(10, columnsStr.split('\n').length))}
                            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                    </label>
                    {summaries.length > 0 && (
                        <div className="overflow-hidden rounded border border-gray-200">
                            <table className="w-full table-fixed text-[11px]">
                                <thead className="bg-gray-100 text-slate-600">
                                    <tr>
                                        <th className="px-2 py-1 text-left font-medium">Column</th>
                                        <th className="w-[84px] px-2 py-1 text-left font-medium">Type</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {summaries.map((cs, idx) => (
                                        <tr key={`${cs.name}:${idx}`} className="border-t border-gray-100">
                                            <td className="truncate px-2 py-1 font-medium text-slate-800">
                                                {cs.name}
                                            </td>
                                            <td className="px-2 py-1">
                                                <select
                                                    value={String(cs.type || 'text')}
                                                    onChange={(e) =>
                                                        setSummaries((prev) =>
                                                            prev.map((p, i) =>
                                                                i === idx
                                                                    ? { ...p, type: e.target.value as ColumnType }
                                                                    : p,
                                                            ),
                                                        )
                                                    }
                                                    className="w-full rounded border border-gray-300 bg-white px-1 py-0.5 text-[11px]"
                                                >
                                                    {COLUMN_TYPES.map((t) => (
                                                        <option key={t} value={t}>
                                                            {t}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <label className="block text-[11px] font-medium text-slate-600">
                        Notes
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={2}
                            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                    </label>
                    <label className="flex items-center gap-2 text-[11px] text-slate-600">
                        <input
                            type="checkbox"
                            checked={locked}
                            onChange={(e) => setLocked(e.target.checked)}
                        />
                        {locked ? (
                            <span className="inline-flex items-center gap-1">
                                <Lock className="h-3 w-3" aria-hidden /> Locked — detector will not overwrite
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1">
                                <Unlock className="h-3 w-3" aria-hidden /> Unlocked — detector may refine on re-sync
                            </span>
                        )}
                    </label>
                    {err && <p className="text-[11px] text-rose-600">{err}</p>}
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            disabled={saving}
                            onClick={handleSave}
                            className="inline-flex items-center gap-1 rounded-md border border-slate-800 bg-slate-900 px-2.5 py-1 text-[11px] font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {saving ? (
                                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                            ) : (
                                <Check className="h-3 w-3" aria-hidden />
                            )}
                            Save
                        </button>
                        <button
                            type="button"
                            disabled={saving}
                            onClick={() => setEditing(false)}
                            className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 shadow-sm transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
